import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  DatePicker,
  Button,
  Select,
  message,
  Spin,
  Table,
  Statistic,
  Progress,
  Alert
} from "antd";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  WarningOutlined,
  UserOutlined,
  TeamOutlined,
  DownloadOutlined,
  TrophyOutlined,
  PieChartOutlined
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import AdminBreadCrumb from "components/AdminBreadCrumb";
import moment from "moment";
import request from "helper/request";
import "./FailedStopAnalysis.scss";

const { RangePicker } = DatePicker;
const { Option } = Select;

const breadCrumbs = [
  { text: "REPORTS", path: "/admin/reports" },
  { text: "FAILED STOPS ANALYSIS" }
];

const SESSION_STORAGE_KEY = "failedStopAnalysisState";

const FailedStopAnalysis = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [driversLoading, setDriversLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    moment().subtract(1, "month").startOf("month"),
    moment().endOf("month")
  ]);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [clients, setClients] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [driverSearchValue, setDriverSearchValue] = useState("");
  const [clientSearchValue, setClientSearchValue] = useState("");

  // Load state from session storage on mount
  useEffect(() => {
    try {
      const savedState = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (savedState) {
        const parsed = JSON.parse(savedState);

        // Restore date range
        if (parsed.dateRange && parsed.dateRange.length === 2) {
          setDateRange([moment(parsed.dateRange[0]), moment(parsed.dateRange[1])]);
        }

        // Restore filters
        if (parsed.selectedDrivers) {
          setSelectedDrivers(parsed.selectedDrivers);
        }
        if (parsed.selectedClients) {
          setSelectedClients(parsed.selectedClients);
        }

        // Restore report data
        if (parsed.reportData) {
          setReportData(parsed.reportData);
        }

        // Restore dropdown data
        if (parsed.drivers) {
          setDrivers(parsed.drivers);
        }
        if (parsed.clients) {
          setClients(parsed.clients);
        }
      }
    } catch (error) {
      console.error("Error loading saved state:", error);
    }
  }, []);

  // Save state to session storage whenever it changes
  useEffect(() => {
    try {
      const stateToSave = {
        dateRange: dateRange ? [dateRange[0]?.format(), dateRange[1]?.format()] : null,
        selectedDrivers,
        selectedClients,
        reportData,
        drivers,
        clients
      };
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stateToSave));
    } catch (error) {
      console.error("Error saving state:", error);
    }
  }, [dateRange, selectedDrivers, selectedClients, reportData, drivers, clients]);

  // Fetch drivers for dropdown
  const fetchDrivers = async (searchValue = "") => {
    if (drivers.length > 0 && !searchValue) return; // Only fetch once unless searching

    setDriversLoading(true);
    try {
      const response = await request.get(`/admin/drivers?limit=1000`);
      setDrivers(response?.data || []);
    } catch (error) {
      console.error("Error fetching drivers:", error);
      message.error("Failed to load drivers");
    } finally {
      setDriversLoading(false);
    }
  };

  // Fetch clients for dropdown
  const fetchClients = async (searchValue = "") => {
    if (clients.length > 0 && !searchValue) return; // Only fetch once unless searching

    setClientsLoading(true);
    try {
      const response = await request.get(`/admin/clients?limit=1000`);
      setClients(response?.data || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
      message.error("Failed to load clients");
    } finally {
      setClientsLoading(false);
    }
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
  };

  const handleSubmit = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.warning("Please select a date range");
      return;
    }

    setLoading(true);
    try {
      const startDate = dateRange[0].format("YYYY-MM-DD");
      const endDate = dateRange[1].format("YYYY-MM-DD");

      const params = new URLSearchParams({
        startDate,
        endDate
      });

      if (selectedDrivers.length > 0) {
        params.append("driverIds", selectedDrivers.join(","));
      }

      if (selectedClients.length > 0) {
        params.append("clientIds", selectedClients.join(","));
      }

      const data = await request.get(`/admin/reports/failed-stops?${params.toString()}`);

      setReportData(data);
      message.success("Failed stops report generated successfully");
    } catch (error) {
      console.error("Error fetching failed stops report:", error);
      message.error("Failed to generate report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const filteredDrivers = drivers.filter((driver) => {
    const fullName = `${driver.firstName || ""} ${driver.lastName || ""}`.toLowerCase();
    return fullName.includes((driverSearchValue || "").toLowerCase());
  });

  const filteredClients = clients.filter((client) => {
    const clientName = (client.clientName || "").toLowerCase();
    return clientName.includes((clientSearchValue || "").toLowerCase());
  });

  // Export to CSV
  const handleExportCSV = () => {
    if (!reportData || !reportData.stops || reportData.stops.length === 0) {
      message.warning("No data to export");
      return;
    }

    // Generate filename
    const dateStr = `${dateRange[0].format("YYYY-MM-DD")}_${dateRange[1].format(
      "YYYY-MM-DD"
    )}`;

    const clientStr =
      selectedClients.length > 0
        ? clients
            .filter((c) => selectedClients.includes(c.id))
            .map((c) => c.clientName)
            .join("-")
            .replace(/[^a-zA-Z0-9-]/g, "")
            .substring(0, 30)
        : "all-clients";

    const driverStr =
      selectedDrivers.length > 0
        ? drivers
            .filter((d) => selectedDrivers.includes(d.id))
            .map((d) => `${d.firstName}${d.lastName}`)
            .join("-")
            .replace(/[^a-zA-Z0-9-]/g, "")
            .substring(0, 30)
        : "all-drivers";

    const filename = `failed-stop-analysis-${dateStr}-${clientStr}-${driverStr}.csv`;

    // Create CSV content
    const headers = [
      "Stop Name",
      "Address",
      "Client",
      "Driver",
      "Route ID",
      "Failed Reason",
      "Est. Arrival",
      "Failed At",
      "Failed Distance"
    ];

    const csvRows = [
      headers.join(","),
      ...reportData.stops.map((stop) => {
        return [
          `"${(stop.stopName || "").replace(/"/g, '""')}"`,
          `"${(stop.address || "").replace(/"/g, '""')}"`,
          `"${(stop.clientName || "").replace(/"/g, '""')}"`,
          `"${(stop.driverName || "").replace(/"/g, '""')}"`,
          `"${(stop.routeId || "").substr(0, 8).toUpperCase()}"`,
          `"${(stop.failedReason || "").replace(/"/g, '""')}"`,
          `"${stop.estimatedArrival || ""}"`,
          `"${stop.failedAt ? moment(stop.failedAt).format("YYYY-MM-DD HH:mm") : ""}"`,
          `"${stop.failedDistance || ""}"`
        ].join(",");
      })
    ];

    const csvContent = csvRows.join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success("Report exported successfully");
  };

  const columns = [
    {
      title: "Stop Name",
      dataIndex: "stopName",
      key: "stopName",
      width: 180,
      fixed: "left"
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      width: 220,
      render: (text) => text || "-"
    },
    {
      title: "Client",
      dataIndex: "clientName",
      key: "clientName",
      width: 150
    },
    {
      title: "Driver",
      dataIndex: "driverName",
      key: "driverName",
      width: 150
    },
    {
      title: "Route ID",
      dataIndex: "routeId",
      key: "routeId",
      width: 100,
      render: (text) => (text ? text.substr(0, 8).toUpperCase() : "-")
    },
    {
      title: "Failed Reason",
      dataIndex: "failedReason",
      key: "failedReason",
      width: 200
    },
    {
      title: "Est. Arrival",
      dataIndex: "estimatedArrival",
      key: "estimatedArrival",
      width: 120
    },
    {
      title: "Failed At",
      dataIndex: "failedAt",
      key: "failedAt",
      width: 180,
      render: (text) => (text ? moment(text).format("YYYY-MM-DD HH:mm") : "-")
    },
    {
      title: "Failed Distance",
      dataIndex: "failedDistance",
      key: "failedDistance",
      width: 130,
      render: (text) => {
        if (!text) return "-";
        const colors = {
          exact: "#52c41a",
          close: "#faad14",
          far: "#ff4d4f"
        };
        return (
          <span style={{ color: colors[text] || "#666", fontWeight: 500 }}>
            {text.charAt(0).toUpperCase() + text.slice(1)}
          </span>
        );
      }
    }
  ];

  return (
    <div className="failed-stop-analysis">
      <AdminBreadCrumb breadCrumbs={breadCrumbs} />

      <div className="failed-stop-analysis-content">
        <div className="failed-stop-analysis-header">
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => history.push("/admin/reports")}
            style={{ padding: 0, marginBottom: "16px" }}
          >
            Back to Reports
          </Button>
          <h1 style={{ color: "#1890ff", marginBottom: "8px" }}>Failed Stops Analysis</h1>
          <p style={{ color: "#666", marginBottom: "32px" }}>
            Analyze failed delivery stops and identify patterns
          </p>
        </div>

        <Card className="filters-card" style={{ marginBottom: "24px" }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={10}>
              <div style={{ marginBottom: "8px" }}>
                <CalendarOutlined
                  style={{ color: "#1890ff", fontSize: "14px", marginRight: "8px" }}
                />
                <span style={{ fontWeight: 500 }}>Date Range *</span>
              </div>
              <RangePicker
                value={dateRange}
                onChange={handleDateChange}
                format="YYYY-MM-DD"
                placeholder={["Start Date", "End Date"]}
                style={{ width: "100%" }}
                disabledDate={(current) => current && current > moment().endOf("day")}
              />
            </Col>

            <Col xs={24} md={7}>
              <div style={{ marginBottom: "8px" }}>
                <UserOutlined
                  style={{ color: "#1890ff", fontSize: "14px", marginRight: "8px" }}
                />
                <span style={{ fontWeight: 500 }}>Driver(s)</span>
              </div>
              <Select
                mode="multiple"
                placeholder="All Drivers"
                value={selectedDrivers}
                onChange={setSelectedDrivers}
                onFocus={() => fetchDrivers()}
                onSearch={setDriverSearchValue}
                loading={driversLoading}
                showSearch
                filterOption={false}
                style={{ width: "100%" }}
                maxTagCount="responsive"
              >
                {filteredDrivers.map((driver) => (
                  <Option key={driver.id} value={driver.id}>
                    {driver.firstName} {driver.lastName}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} md={7}>
              <div style={{ marginBottom: "8px" }}>
                <TeamOutlined
                  style={{ color: "#1890ff", fontSize: "14px", marginRight: "8px" }}
                />
                <span style={{ fontWeight: 500 }}>Client(s)</span>
              </div>
              <Select
                mode="multiple"
                placeholder="All Clients"
                value={selectedClients}
                onChange={setSelectedClients}
                onFocus={() => fetchClients()}
                onSearch={setClientSearchValue}
                loading={clientsLoading}
                showSearch
                filterOption={false}
                style={{ width: "100%" }}
                maxTagCount="responsive"
              >
                {filteredClients.map((client) => (
                  <Option key={client.id} value={client.id}>
                    {client.clientName}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>

          <Row style={{ marginTop: "16px" }}>
            <Col span={24}>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={loading}
                disabled={!dateRange || dateRange.length !== 2}
                style={{ width: "100%" }}
                icon={<WarningOutlined />}
              >
                Generate Report
              </Button>
            </Col>
          </Row>
        </Card>

        {loading && (
          <div
            className="loading-container"
            style={{ textAlign: "center", padding: "60px 0" }}
          >
            <Spin size="large" tip="Generating failed stops report..." />
          </div>
        )}

        {reportData && !loading && (
          <div className="report-results">
            <div
              style={{
                marginBottom: "24px"
              }}
            >
              <h2 style={{ color: "#1890ff", margin: 0 }}>
                Report: {dateRange[0].format("MMM DD, YYYY")} -{" "}
                {dateRange[1].format("MMM DD, YYYY")}
              </h2>
            </div>

            {/* Data Limit Warning */}
            {reportData.summary?.dataLimitReached && (
              <Alert
                message="Data Limit Reached"
                description={reportData.summary.dataLimitMessage}
                type="warning"
                showIcon
                style={{ marginBottom: "24px" }}
              />
            )}

            {/* Summary Section */}
            <Row gutter={[24, 24]} style={{ marginBottom: "24px" }}>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Total Failed Stops"
                    value={reportData.summary?.totalFailedStops || 0}
                    prefix={<WarningOutlined style={{ color: "#ff4d4f" }} />}
                    valueStyle={{ color: "#ff4d4f" }}
                    suffix={
                      reportData.summary?.totalStops
                        ? `/ ${reportData.summary.totalStops}`
                        : ""
                    }
                  />
                  {reportData.summary?.failureRate !== undefined && (
                    <div style={{ marginTop: "8px", fontSize: "14px", color: "#666" }}>
                      Failure Rate: {reportData.summary.failureRate.toFixed(2)}%
                    </div>
                  )}
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Unique Routes"
                    value={reportData.summary?.uniqueRoutes || 0}
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Affected Clients"
                    value={reportData.summary?.affectedClients || 0}
                    valueStyle={{ color: "#fa8c16" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="Affected Drivers"
                    value={reportData.summary?.affectedDrivers || 0}
                    valueStyle={{ color: "#722ed1" }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Analytics Section */}
            {reportData.analytics && (
              <>
                {/* Top 5 Failed Reasons */}
                <Card
                  title={
                    <span>
                      <PieChartOutlined
                        style={{ marginRight: "8px", color: "#1890ff" }}
                      />
                      Top 5 Failed Reasons
                    </span>
                  }
                  style={{ marginBottom: "24px" }}
                >
                  {reportData.analytics.topFailedReasons?.length > 0 ? (
                    <div>
                      {reportData.analytics.topFailedReasons.map((reason, index) => (
                        <div
                          key={index}
                          style={{
                            marginBottom:
                              index < reportData.analytics.topFailedReasons.length - 1
                                ? "16px"
                                : "0"
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: "4px"
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>
                              {index + 1}. {reason.reason}
                            </span>
                            <span style={{ color: "#666" }}>
                              {reason.count} stops ({reason.percentage.toFixed(1)}%)
                            </span>
                          </div>
                          <Progress
                            percent={reason.percentage}
                            strokeColor="#ff4d4f"
                            showInfo={false}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{ textAlign: "center", color: "#999", padding: "20px 0" }}
                    >
                      No failed reasons data available
                    </div>
                  )}
                </Card>

                {/* Top 5 Drivers with Failures */}
                <Card
                  title={
                    <span>
                      <TrophyOutlined style={{ marginRight: "8px", color: "#fa8c16" }} />
                      Top 5 Drivers with Failures
                    </span>
                  }
                  style={{ marginBottom: "24px" }}
                >
                  {reportData.analytics.topDriversWithFailures?.length > 0 ? (
                    <Row gutter={[16, 16]}>
                      {reportData.analytics.topDriversWithFailures.map(
                        (driver, index) => (
                          <Col xs={24} md={12} lg={12} key={driver.driverId}>
                            <Card size="small" style={{ backgroundColor: "#fafafa" }}>
                              <div style={{ marginBottom: "8px" }}>
                                <strong style={{ fontSize: "16px" }}>
                                  {index + 1}. {driver.driverName}
                                </strong>
                              </div>
                              <div style={{ marginBottom: "12px" }}>
                                <div style={{ color: "#666", fontSize: "14px" }}>
                                  <div style={{ marginBottom: "4px" }}>
                                    Failed: <strong>{driver.failedCount}</strong> /{" "}
                                    {driver.totalStops} stops
                                  </div>
                                  <div style={{ marginBottom: "8px" }}>
                                    Individual Failure Rate:{" "}
                                    <strong style={{ color: "#ff4d4f" }}>
                                      {driver.individualFailureRate.toFixed(1)}%
                                    </strong>
                                  </div>
                                </div>
                                <Progress
                                  percent={driver.individualFailureRate}
                                  strokeColor="#ff4d4f"
                                  size="small"
                                />
                              </div>
                              <div
                                style={{
                                  borderTop: "1px solid #e8e8e8",
                                  paddingTop: "8px",
                                  fontSize: "13px",
                                  color: "#666"
                                }}
                              >
                                Contribution to total failures:{" "}
                                <strong style={{ color: "#fa8c16" }}>
                                  {driver.contributionToTotalFailures.toFixed(1)}%
                                </strong>
                              </div>
                            </Card>
                          </Col>
                        )
                      )}
                    </Row>
                  ) : (
                    <div
                      style={{ textAlign: "center", color: "#999", padding: "20px 0" }}
                    >
                      No driver failure data available
                    </div>
                  )}
                </Card>

                {/* Day of Week Analysis */}
                <Card
                  title={
                    <span>
                      <CalendarOutlined
                        style={{ marginRight: "8px", color: "#52c41a" }}
                      />
                      Day of Week Analysis
                    </span>
                  }
                  style={{ marginBottom: "24px" }}
                >
                  {reportData.analytics.dayOfWeekBreakdown?.length > 0 ? (
                    <div>
                      {reportData.analytics.dayOfWeekBreakdown.map((day) => (
                        <div
                          key={day.dayOfWeek}
                          style={{
                            marginBottom: day.dayOfWeek < 6 ? "16px" : "0"
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: "4px"
                            }}
                          >
                            <span style={{ fontWeight: 500, minWidth: "100px" }}>
                              {day.dayName}
                            </span>
                            <span style={{ color: "#666" }}>
                              {day.count} stops ({day.percentage.toFixed(1)}%)
                            </span>
                          </div>
                          <Progress
                            percent={day.percentage}
                            strokeColor={
                              day.dayOfWeek === 5 || day.dayOfWeek === 6
                                ? "#faad14"
                                : "#52c41a"
                            }
                            showInfo={false}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{ textAlign: "center", color: "#999", padding: "20px 0" }}
                    >
                      No day of week data available
                    </div>
                  )}
                </Card>
              </>
            )}

            {/* Stop Details Section */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px"
              }}
            >
              <h3 style={{ color: "#1890ff", margin: 0 }}>Stop Details</h3>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExportCSV}
              >
                Export CSV
              </Button>
            </div>

            {/* Results Table */}
            <Card>
              <Table
                columns={columns}
                dataSource={reportData.stops || []}
                rowKey="id"
                scroll={{ x: 1600 }}
                pagination={{
                  pageSize: 50,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} failed stops`
                }}
                onRow={(record) => ({
                  onClick: () => {
                    if (record.routeId && record.jobId) {
                      history.push(`/admin/routes/${record.routeId}/${record.jobId}`);
                    }
                  },
                  style: { cursor: "pointer" }
                })}
              />
            </Card>
          </div>
        )}

        {!reportData && !loading && (
          <Card className="empty-state">
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <WarningOutlined
                style={{ fontSize: "48px", color: "#d9d9d9", marginBottom: "16px" }}
              />
              <h3 style={{ color: "#666" }}>No Report Generated</h3>
              <p style={{ color: "#999" }}>
                Select filters and click Generate Report to analyze failed stops
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FailedStopAnalysis;
